import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, useIntl } from 'react-intl';
import usePrevious from 'use-previous';

import { allowFreeText } from '@bibliocommons/utils-user';
import { catalogBibShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { SecondaryGhostButton } from '@bibliocommons/base-buttons';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import TextUgcEditor from '../TextUgcEditor';

import './SummaryEditor.scss';

const EDITOR_KEY = 'summary';

export default function SummaryEditor(props) {
  const { open, currentUser, initialContent, summariesTransactions, catalogBib, summaryId } = props;

  const intl = useIntl();
  const prevProps = usePrevious(props);
  const addButtonRef = useRef();
  const editorRef = useRef();

  useEffect(() => {
    // Set focus on the form when it opens
    if (open && !prevProps?.open && editorRef.current) {
      setFocusWithinElement(editorRef.current);
    }

    // Set focus on the add button when the form is closed
    if (!open && prevProps?.open && addButtonRef.current) {
      setFocusWithinElement(addButtonRef.current);
    }
  }, [open, prevProps, editorRef, addButtonRef]);

  const handleSuccess = () => {
    if (!props.summaryId) props.ugcEditorActions.closeEditor(EDITOR_KEY);
    if (props.handleSuccess) props.handleSuccess();
  };

  const handleCancel = () => {
    if (!props.summaryId) props.ugcEditorActions.closeEditor(EDITOR_KEY);
    if (props.handleCancel) props.handleCancel();
  };

  const addSummary = summaryContent => {
    props.summariesActions.addSummary({
      metadataId: props.catalogBib.get('metadataId'),
      summary: summaryContent,
      postSuccess: handleSuccess
    });
  };

  const updateSummary = summaryContent => {
    props.summariesActions.updateSummary({
      metadataId: props.catalogBib.get('metadataId'),
      summaryId: props.summaryId,
      summary: summaryContent,
      postSuccess: handleSuccess
    });
  };

  const renderCollapsed = () => {
    return (
      <SecondaryGhostButton
        ref={addButtonRef}
        dataKey="add-ugc"
        handleClick={() => props.ugcEditorActions.openEditor(EDITOR_KEY)}
      >
        <FormattedMessage id="add_summary" />
      </SecondaryGhostButton>
    );
  };

  const renderExpanded = () => {
    const metadataId = catalogBib.get('metadataId');
    const isLoading =
      summariesTransactions.getIn([metadataId, 'isAdding']) ||
      summariesTransactions.getIn([metadataId, summaryId, 'isUpdating']);
    const label = summaryId
      ? intl.formatMessage({ id: 'edit_summary_prompt' })
      : intl.formatMessage({ id: 'add_summary_prompt' });

    return (
      <TextUgcEditor
        ugcType="summary"
        ref={editorRef}
        handleConfirm={summaryId ? updateSummary : addSummary}
        handleCancel={handleCancel}
        label={label}
        isLoading={isLoading}
        initialContent={initialContent}
      />
    );
  };

  if (!currentUser || allowFreeText(currentUser)) {
    return <div className="cp-summary-editor">{open ? renderExpanded() : renderCollapsed()}</div>;
  }

  return null;
}

SummaryEditor.defaultProps = {
  initialContent: ''
};

SummaryEditor.propTypes = {
  catalogBib: catalogBibShape,
  currentUser: userIdentityShape,
  summaryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSuccess: PropTypes.func,
  handleCancel: PropTypes.func,
  initialContent: PropTypes.string,
  open: PropTypes.bool,
  summariesTransactions: ImmutablePropTypes.map.isRequired,
  summariesActions: PropTypes.objectOf(PropTypes.func),
  ugcEditorActions: PropTypes.objectOf(PropTypes.func)
};
